import { Pagination } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import { useUserFeatures } from "../../../../components/contexts/UserFeaturesContext";
import DocumentList from "../../../../components/documents/DocumentList";
import DocumentsPage from "../DocumentsPage";
import SsoRedirectAccountList from "../../../../components/SsoRedirectAccountList";
import SignedUrlIframe from "../../../../components/SignedUrlIframe";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
};

type DocumentsPageContainerProps = {
  documentsJson: string;
  isHtmlJson: "true" | "false";
  sso: string;
  supportsSsoPerAccount: string;
  supportsSsoTaxDocument: string;
  accountNamesAndNumbersJson: string;
  paginationCurrentPage?: string;
  paginationTotalPages?: string;
  paginationHasPrevious?: string;
  paginationHasNext?: string;
};

const DocumentsPageContainer = ({
  documentsJson,
  isHtmlJson,
  sso,
  supportsSsoPerAccount,
  supportsSsoTaxDocument,
  accountNamesAndNumbersJson,
  paginationCurrentPage,
  paginationTotalPages,
  paginationHasPrevious,
  paginationHasNext,
}: DocumentsPageContainerProps) => {
  const { sendNotification } = useNotificationContext();
  const features = useUserFeatures();
  const isSso = sso === "true";
  const isSsoPerAccount = supportsSsoPerAccount === "true";
  const enableDocumentFilters = features.enable_document_filters;

  const createPageUrl = (page: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", page.toString());
    return `?${params.toString()}`;
  };

  // Parse pagination props
  const pagination: PaginationProps | null =
    paginationCurrentPage && paginationTotalPages
      ? {
          currentPage: parseInt(paginationCurrentPage, 10),
          totalPages: parseInt(paginationTotalPages, 10),
          hasPrevious: paginationHasPrevious === "true",
          hasNext: paginationHasNext === "true",
        }
      : null;

  // SSO with per-account support
  if (isSso && isSsoPerAccount) {
    return (
      <div className="ui centered grid container">
        <SsoRedirectAccountList
          accountNamesAndNumbersJson={accountNamesAndNumbersJson}
          supportsSsoTaxDocumentJson={supportsSsoTaxDocument}
        />
      </div>
    );
  }

  // SSO iframe fallback
  if (isSso) {
    return (
      <div className="ui centered grid container" style={{ width: "100%" }}>
        <SignedUrlIframe
          sendNotification={sendNotification}
          endpoint="signed_urls/documents/list"
          height="1000px"
        />
      </div>
    );
  }

  // Note that we'll be adding pagination support in:
  // https://linear.app/narmi/issue/CDB-3669/add-pdf-document-ui-pagination-support
  // Document filters feature enabled
  if (enableDocumentFilters) {
    return (
      <DocumentsPage documentsJson={documentsJson} isHtmlJson={isHtmlJson} />
    );
  }

  // Default: basic document list with optional pagination
  return (
    <div className="ui centered grid container">
      <DocumentList documentsJson={documentsJson} isHtmlJson={isHtmlJson} />
      {pagination && pagination.totalPages > 1 ? (
        <Pagination
          totalPages={pagination.totalPages}
          defaultSelectedPage={pagination.currentPage}
          onPageChange={(newPage: number) => {
            window.location.href = createPageUrl(newPage);
          }}
        />
      ) : null}
    </div>
  );
};

export default DocumentsPageContainer;
